import React, { useState, useEffect } from 'react';
import { _T, C } from '../../shared/theme';
import { s_GC, s_PL } from '../../shared/styles';
import MemoryGame from './components/MemoryGame';
import ReactionGame from './components/ReactionGame';
import MathGame from './components/MathGame';
import SequenceGame from './components/SequenceGame';
import StroopGame from './components/StroopGame';
import BreathPacerGame from './components/BreathPacerGame';
import FocusTapGame from './components/FocusTapGame';
import OddSymbolGame from './components/OddSymbolGame';
import DirectionFlashGame from './components/DirectionFlashGame';
import PeripheralFlashGame from './components/PeripheralFlashGame';
import NumChainGame from './components/NumChainGame';
import WordMemoryGame from './components/WordMemoryGame';
import ToneDecoderGame from './components/ToneDecoderGame';
import EmpathyMirrorGame from './components/EmpathyMirrorGame';
import CoopBreathGame from './components/CoopBreathGame';
import EmotionMatchGame from './components/EmotionMatchGame';
import PatternMatchGame from './components/PatternMatchGame';
import ColorFloodGame from './components/ColorFloodGame';
import MentalMazeGame from './components/MentalMazeGame';
import DualBackGame from './components/DualBackGame';
import SpeedSortGame from './components/SpeedSortGame';
import SoundReactGame from './components/SoundReactGame';
import SoundMemoryGame from './components/SoundMemoryGame';
import PitchMatchGame from './components/PitchMatchGame';
import RhythmCopyGame from './components/RhythmCopyGame';
import SoundLocateGame from './components/SoundLocateGame';
import TimingGame from './components/TimingGame';
import FlashEquationGame from './components/FlashEquationGame';
import GridMemoryGame from './components/GridMemoryGame';
import GameScoresTab from './GameScoresTab';
import GameTrainingTab from './GameTrainingTab';
import GameRoom from './GameRoom';
import GameVoiceChat from './GameVoiceChat';
import { GAME_DEFS, GAME_CATS } from './constants';
import MultiplayerInviteModal from '../../modals/MultiplayerInviteModal';
import { getGameSession, stopGameSession } from '../../shared/dbService';
import { playSound } from '../../shared/soundEngine';
import { s_GB } from '../../shared/styles';
import { getGameLevel, saveGameScore } from '../../shared/utils';

// Maps a game title (from invite) to a game id
const TITLE_TO_ID = {};
GAME_DEFS.forEach(g => {
  TITLE_TO_ID[g.label]   = g.id;
  TITLE_TO_ID[g.labelNL] = g.id;
});

function GamesScreen({ lang, allUsers, friends, currentUserId, userName, pendingGameInvite, onClearGameInvite, presetPartner, onClearPresetPartner, license, walletBalance, onSpendCredit }) {
  const nl = lang === "nl";
  const [activeGame, setActiveGame] = useState(null);
  const [creditError, setCreditError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [catFilt, setCatFilt] = useState("all");
  const [roomPartner, setRoomPartner] = useState(null); // partner arriving from game room (has _token)
  const [pendingRoom, setPendingRoom] = useState(null); // joiner: waiting room data
  const [pendingGameId, setPendingGameId] = useState(null); // game waiting for invite to complete
  const [livePartner, setLivePartner] = useState(null); // real partner reported by game component via onRealPartner
  const [sessionStopped, setSessionStopped] = useState(false); // partner stopped the session

  // When arriving via game invite link, show the GameRoom (joiner mode)
  useEffect(() => {
    if (!pendingGameInvite) return;
    setPendingRoom(pendingGameInvite);
    if (onClearGameInvite) onClearGameInvite();
  }, [pendingGameInvite]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop game and apply focus penalty when user switches tab or navigates away
  useEffect(() => {
    if (!activeGame) return;
    const handleVisibility = () => {
      if (document.hidden) {
        // Record a score of 0 — abandoned session penalises focus pillar averages
        saveGameScore(activeGame, 0);
        setActiveGame(null);
        setRoomPartner(null);
        setLivePartner(null);
        setSessionStopped(false);
        if (onClearPresetPartner) onClearPresetPartner();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [activeGame, onClearPresetPartner]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for session stopped by partner
  useEffect(() => {
    const token = (livePartner?._token) || (roomPartner && !roomPartner.isAI ? roomPartner._token : null);
    if (!token || !activeGame) return;
    const id = setInterval(async () => {
      const data = await getGameSession(token);
      if (data?.status === 'stopped') setSessionStopped(true);
    }, 4000);
    return () => clearInterval(id);
  }, [livePartner, roomPartner, activeGame]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStopSession = async () => {
    if (vcToken) await stopGameSession(vcToken);
    setActiveGame(null); setRoomPartner(null); setLivePartner(null); setSessionStopped(false);
    if (onClearPresetPartner) onClearPresetPartner();
  };

  const gameProps = {
    lang,
    onBack: () => { playSound('game_win'); setActiveGame(null); setRoomPartner(null); setLivePartner(null); setSessionStopped(false); if (onClearPresetPartner) onClearPresetPartner(); },
    allUsers,
    friends,
    currentUserId,
    userName,
    invitePartner: roomPartner, // only pass partners that have a valid _token (from GameRoom)
    onRealPartner: (p) => setLivePartner(p && !p.isAI && p._token ? p : null),
  };

  // ── Joiner: show GameRoom waiting lobby ───────────────────────────────────
  if (pendingRoom && !activeGame) {
    const gameId = TITLE_TO_ID[pendingRoom.gameTitle];
    const gd = GAME_DEFS.find(g => g.id === gameId);
    return (
      <GameRoom
        token={pendingRoom.token}
        lang={lang}
        currentUser={{ id: currentUserId, name: userName }}
        isInviter={false}
        gameTitle={pendingRoom.gameTitle}
        gameIcon={gd?.icon || '🎮'}
        onStart={(partner, m) => {
          setRoomPartner({ ...partner, _roomMode: m, _token: pendingRoom.token, _isInviter: false });
          setPendingRoom(null);
          setActiveGame(gameId);
        }}
        onLeave={() => setPendingRoom(null)}
      />
    );
  }

  // Show voice chat overlay when playing with a real (non-AI) partner
  // livePartner: set by game component (covers in-game "Together" button path for inviter)
  // roomPartner: set by GameRoom join path (covers joiner path)
  const vcPartner = livePartner || (roomPartner && !roomPartner.isAI ? roomPartner : null);
  const vcToken   = vcPartner?._token || null;
  const vcRole    = vcPartner?._isInviter ? 'inviter' : 'joiner';
  const voiceChatPartner = activeGame ? vcPartner : null;

  if (activeGame) {
    const lvl = getGameLevel(activeGame);
    const difficulty = lvl.level >= 7 ? 3 : lvl.level >= 4 ? 2 : 1;
    const diffProps = { ...gameProps, difficulty };
    const gameMap = {
      memory:         <MemoryGame {...diffProps}/>,
      reaction:       <ReactionGame {...diffProps}/>,
      math:           <MathGame {...gameProps}/>,
      sequence:       <SequenceGame {...gameProps}/>,
      stroop:         <StroopGame {...gameProps}/>,
      breathgame:     <BreathPacerGame {...gameProps}/>,
      focustap:       <FocusTapGame {...gameProps}/>,
      oddsymbol:      <OddSymbolGame {...gameProps}/>,
      directionflash: <DirectionFlashGame {...gameProps}/>,
      peripheralflash:<PeripheralFlashGame {...gameProps}/>,
      numchain:       <NumChainGame {...gameProps}/>,
      wordmemory:     <WordMemoryGame {...gameProps}/>,
      tonedecoder:    <ToneDecoderGame {...gameProps}/>,
      empathymirror:  <EmpathyMirrorGame {...gameProps}/>,
      coopbreath:     <CoopBreathGame {...gameProps}/>,
      emotionmatch:   <EmotionMatchGame {...gameProps}/>,
      patternmatch:   <PatternMatchGame {...gameProps}/>,
      colorflood:     <ColorFloodGame {...gameProps}/>,
      mentalmaze:     <MentalMazeGame {...gameProps}/>,
      dualback:       <DualBackGame {...gameProps}/>,
      speedsort:      <SpeedSortGame {...gameProps}/>,
      soundreact:     <SoundReactGame {...gameProps}/>,
      soundmemory:    <SoundMemoryGame {...gameProps}/>,
      pitchmatch:     <PitchMatchGame {...gameProps}/>,
      rhythmcopy:     <RhythmCopyGame {...gameProps}/>,
      soundlocate:    <SoundLocateGame {...gameProps}/>,
      timinggame:     <TimingGame {...gameProps}/>,
      flashequation:  <FlashEquationGame {...gameProps}/>,
      gridmemory:     <GridMemoryGame {...gameProps}/>,
    };
    const gd = GAME_DEFS.find(g => g.id === activeGame);

    return (
      <div style={{ position: "relative" }}>
        {/* Partner stopped session overlay */}
        {sessionStopped && (
          <div style={{ position: "absolute", inset: 0, zIndex: 50, background: "rgba(10,18,32,0.92)", borderRadius: 16, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 24 }}>
            <div style={{ fontSize: 48 }}>🚪</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: 18, color: "#EEF3FF", textAlign: "center" }}>{nl ? "Partner heeft de sessie beëindigd" : "Partner ended the session"}</div>
            <button style={s_GB({ fontSize: 13, padding: "10px 24px" })} onClick={() => { setSessionStopped(false); setActiveGame(null); setRoomPartner(null); setLivePartner(null); if (onClearPresetPartner) onClearPresetPartner(); }}>
              ← {nl ? "Terug naar spellen" : "Back to games"}
            </button>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: 22, color: "#EEF3FF" }}>
            {gd?.icon} {nl ? gd?.labelNL : gd?.label}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {voiceChatPartner && (
              <GameVoiceChat
                partner={voiceChatPartner}
                currentUserId={currentUserId}
                token={vcToken}
                myRole={vcRole}
                lang={lang}
              />
            )}
            {vcToken && (
              <button onClick={handleStopSession} style={s_GB({ fontSize: 11, padding: "5px 10px", borderRadius: 10, background: "rgba(192,80,80,0.15)", border: "1px solid #C05050", color: "#C05050" })}>
                ⏹ {nl ? "Stop" : "Stop"}
              </button>
            )}
          </div>
        </div>
        {gameMap[activeGame]}
      </div>
    );
  }

  const filtered = GAME_DEFS.filter(g => catFilt === "all" || g.catGroup === catFilt);
  const byCategory = {};
  filtered.forEach(g => { if (!byCategory[g.catGroup]) byCategory[g.catGroup] = []; byCategory[g.catGroup].push(g); });

  return (
    <div style={{ paddingBottom: 8 }}>
      {/* Freemium gate notice */}
      {(!license || license === 'freemium') && !presetPartner && (
        <div style={{ background: "rgba(200,168,75,0.08)", border: "1px solid rgba(200,168,75,0.3)", borderRadius: 14, padding: "12px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 20 }}>💎</span>
          <div>
            <div style={{ color: "#C8A84B", fontSize: 12, fontWeight: 700, marginBottom: 2 }}>
              {nl ? "Gratis spellen beschikbaar" : "Free games available"}
            </div>
            <div style={{ color: "#8899BB", fontSize: 11 }}>
              {nl ? "Niet-premium spellen zijn gratis. Upgrade voor toegang tot alle premium spellen." : "Non-premium games are free. Upgrade to unlock all premium games."}
            </div>
          </div>
        </div>
      )}

      {presetPartner && (
        <div style={{ background: `linear-gradient(135deg,${_T.grad1}22,${_T.grad2}22)`, border: `1px solid ${_T.grad1}66`, borderRadius: 14, padding: "12px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 22 }}>🎮</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: "#EEF3FF", fontSize: 13, fontWeight: 600 }}>{nl ? `Speel samen met ${presetPartner.name}` : `Play together with ${presetPartner.name}`}</div>
            <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>{nl ? "Kies een spel om te beginnen" : "Choose a game to get started"}</div>
          </div>
          <button onClick={onClearPresetPartner} style={{ background: "none", border: "none", color: C.silver, cursor: "pointer", fontSize: 18, padding: 4 }}>✕</button>
        </div>
      )}

      {/* ══ TAB BAR ══ */}
      <div style={{ display: "flex", background: "rgba(22,37,64,0.65)", borderRadius: 14, padding: 4, marginBottom: 14, gap: 2 }}>
        {[
          { id: "overview", icon: "🎮", label: nl ? "Overzicht" : "Overview" },
          { id: "scores",   icon: "🏆", label: "Scores" },
          { id: "training", icon: "📈", label: "Training" },
        ].map(t => (
          <button key={t.id} onClick={() => { playSound('tab'); setTab(t.id); }} style={{
            flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 2, padding: "9px 4px", borderRadius: 10, border: "none", cursor: "pointer",
            background: tab === t.id ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : "transparent",
          }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>{t.icon}</span>
            <span style={{ fontSize: 9, fontWeight: tab === t.id ? 700 : 400, whiteSpace: "nowrap", fontFamily: "Inter,sans-serif", color: tab === t.id ? "#0a1220" : C.silver }}>{t.label}</span>
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div>
          <div style={{ display: "flex", gap: 4, flexWrap: "nowrap", overflowX: "auto", marginBottom: 14, paddingBottom: 4 }}>
            <button style={s_PL(catFilt === "all")} onClick={() => { playSound('filter'); setCatFilt("all"); }}>{nl ? "Alles" : "All"}</button>
            {GAME_CATS.map(cg => (
              <button key={cg.id} style={{ ...s_PL(catFilt === cg.id, cg.color), flexShrink: 0 }} onClick={() => { playSound('filter'); setCatFilt(cg.id); }}>{cg.icon} {nl ? cg.label : cg.labelEN}</button>
            ))}
          </div>
          {(()=>{
            // Total XP earned across all games — used for unlock gating
            const totalXp = GAME_DEFS.reduce((sum, g) => sum + ((getGameLevel(g.id).xp) || 0), 0);
            return Object.entries(byCategory).map(([cgId, games]) => {
            const cg = GAME_CATS.find(x => x.id === cgId) || { label: cgId, labelEN: cgId, icon: "🎮", color: "#C8A84B" };
            return (
              <div key={cgId} style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: `${cg.color}20`, border: `1px solid ${cg.color}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{cg.icon}</div>
                  <span style={{ color: cg.color, fontSize: 11, fontWeight: 700, letterSpacing: 2 }}>{(nl ? cg.label : cg.labelEN).toUpperCase()}</span>
                  <div style={{ flex: 1, height: 1, background: `${cg.color}30` }} />
                </div>
                <div className="sl-cards-grid">
                {games.map(g => {
                  const lvl        = getGameLevel(g.id);
                  const isMultiOnly = !!g.multiplayerOnly;
                  const isPremium   = !!g.premium;
                  const isFreemium  = !license || license === 'freemium';
                  const isPremLocked = isPremium && isFreemium;
                  const isXpLocked   = g.unlockXp && totalXp < g.unlockXp;
                  const isLocked     = isPremLocked || isXpLocked;

                  const handleLaunch = async () => {
                    if (isPremLocked) { setCreditError(nl ? 'Dit spel is beschikbaar voor premium gebruikers.' : 'This game is available for premium users.'); return; }
                    if (isXpLocked)   { setCreditError(nl ? `Nog ${g.unlockXp - totalXp} XP nodig om dit spel te ontgrendelen.` : `You need ${g.unlockXp - totalXp} more XP to unlock this game.`); return; }
                    if (isMultiOnly) { setPendingGameId(g.id); return; }
                    if (presetPartner) { setPendingGameId(g.id); return; }
                    if (onSpendCredit && !isFreemium) {
                      const ok = await onSpendCredit(1, `game_${g.id}`);
                      if (!ok) { setCreditError(nl ? 'Onvoldoende credits.' : 'Not enough credits.'); return; }
                    }
                    playSound('game_start');
                    setActiveGame(g.id);
                  };
                  const xpInLevel = (lvl.xp || 0) % 100;
                  const hasPlayed = (lvl.xp || 0) > 0;
                  // XP progress toward unlock threshold
                  const xpUnlockPct = g.unlockXp ? Math.min(100, Math.round((totalXp / g.unlockXp) * 100)) : 100;

                  return (
                    <div key={g.id} onClick={handleLaunch} style={{ ...s_GC({ cursor: "pointer", padding: 14, marginBottom: 8 }), ...(presetPartner && !isMultiOnly ? { border: `1px solid ${_T.grad1}55`, boxShadow: `0 0 12px ${_T.grad1}22` } : {}), ...(isLocked ? { opacity: 0.75 } : {}) }}>
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <div style={{ width: 48, height: 48, borderRadius: 14, background: `${g.color}20`, border: `1px solid ${g.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
                            {isLocked ? "🔒" : g.icon}
                          </div>
                          {!isLocked && lvl.level > 1 && (
                            <div style={{ position: "absolute", bottom: -4, right: -4, background: g.color, borderRadius: 8, fontSize: 9, fontWeight: 700, color: "#0a1220", padding: "1px 5px", border: "1px solid rgba(0,0,0,0.3)" }}>
                              Lv{lvl.level}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: "#EEF3FF", fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{nl ? g.labelNL : g.label}</div>
                          <div style={{ color: "#8899BB", fontSize: 11, lineHeight: 1.4 }}>{nl ? g.descNL : g.desc}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                            {isPremLocked
                              ? <span style={{ fontSize: 11, color: "#C8A84B" }}>⭐ Premium</span>
                              : isXpLocked
                                ? <span style={{ fontSize: 11, color: "#5B9ED0" }}>🔓 {nl ? `Ontgrendelt op ${g.unlockXp} XP` : `Unlocks at ${g.unlockXp} XP`}</span>
                                : isMultiOnly
                                  ? <span style={{ fontSize: 11, color: "#5B9ED0" }}>👥 {nl ? "Samen spelen" : "Play together"}</span>
                                  : <>
                                      <span style={{ fontSize: 11, color: "#C8A84B" }}>👥 {nl ? "Solo & samen" : "Solo & together"}</span>
                                      {!isFreemium && <span style={{ fontSize: 10, color: "#C8A84B", background: "rgba(200,168,75,0.12)", border: "1px solid rgba(200,168,75,0.3)", borderRadius: 20, padding: "1px 6px", fontWeight: 700 }}>⚡1</span>}
                                    </>
                            }
                          </div>
                          {/* XP unlock progress bar (for XP-gated games not yet unlocked) */}
                          {isXpLocked && (
                            <div style={{ marginTop: 8 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                <span style={{ fontSize: 9, color: "#8899BB", letterSpacing: 1 }}>{nl ? "VOORTGANG" : "PROGRESS"}</span>
                                <span style={{ fontSize: 9, color: "#5B9ED0" }}>{totalXp}/{g.unlockXp} XP</span>
                              </div>
                              <div style={{ height: 4, borderRadius: 4, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${xpUnlockPct}%`, borderRadius: 4, background: "linear-gradient(90deg,#5B9ED099,#5B9ED0)", transition: "width 0.6s ease" }}/>
                              </div>
                            </div>
                          )}
                          {/* In-game level XP bar (for unlocked games) */}
                          {!isLocked && (
                            <div style={{ marginTop: 8 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                                <span style={{ fontSize: 9, color: "#8899BB", fontWeight: 600, letterSpacing: 1 }}>
                                  {nl ? `NIVEAU ${lvl.level}` : `LEVEL ${lvl.level}`}
                                </span>
                                <span style={{ fontSize: 9, color: g.color }}>
                                  {hasPlayed ? `${xpInLevel}/100 XP` : (nl ? "Nog niet gespeeld" : "Not played yet")}
                                </span>
                              </div>
                              <div style={{ height: 4, borderRadius: 4, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${xpInLevel}%`, borderRadius: 4, background: `linear-gradient(90deg, ${g.color}99, ${g.color})`, transition: "width 0.4s ease" }} />
                              </div>
                            </div>
                          )}
                        </div>
                        <span style={{ color: "#8899BB", fontSize: 20 }}>›</span>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            );
          });})()}
        </div>
      )}
      {tab === "scores"   && <GameScoresTab lang={lang} />}
      {tab === "training" && <GameTrainingTab lang={lang} />}
      {pendingGameId && (
        <MultiplayerInviteModal
          lang={lang}
          title={nl ? (GAME_DEFS.find(g => g.id === pendingGameId)?.labelNL) : (GAME_DEFS.find(g => g.id === pendingGameId)?.label)}
          icon={GAME_DEFS.find(g => g.id === pendingGameId)?.icon || '🎮'}
          allUsers={allUsers}
          friends={friends}
          defaultFriend={presetPartner}
          onInvite={(user, m) => {
            setRoomPartner(user);
            setPendingGameId(null);
            setActiveGame(pendingGameId);
            if (onClearPresetPartner) onClearPresetPartner();
          }}
          onClose={() => setPendingGameId(null)}
        />
      )}
      {/* Credit / premium error — vertically centered overlay */}
      {creditError && (
        <div onClick={() => setCreditError(null)} style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 20px", background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#1e3050", border: "1px solid #C05050", borderRadius: 20, padding: "24px 22px", maxWidth: 340, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, boxShadow: "0 16px 48px rgba(0,0,0,0.7)", textAlign: "center" }}>
            <span style={{ fontSize: 36 }}>💎</span>
            <span style={{ color: "#EEF3FF", fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>{creditError}</span>
            <button onClick={() => setCreditError(null)} style={{ marginTop: 6, background: "none", border: "1px solid #8899BB44", color: "#8899BB", cursor: "pointer", fontSize: 13, padding: "8px 24px", borderRadius: 10 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GamesScreen;
